import React from 'react'
import papa from 'papaparse'
import { useLazyQuery } from '@apollo/client'
import { RouteComponentProps } from 'react-router-dom'
import Icon from 'components/Icon'
import TextInput from 'components/TextInput'
import {
  getFormattedDateAndTime,
  SortDirectionEnum,
  useStateWithDebounce,
  MarketingAnalytics_marketingAnalytics_users,
  useMarketingAnalytics,
  MarketingAnalytics as MarketingAnalyticsCsv,
  MarketingAnalyticsVariables as MarketingAnalyticsVariablesCsv,
  UserPermissions,
  useAuth,
} from '@modmd/data'
import { differenceInCalendarYears } from 'date-fns'
import { useSortChangeHandler } from 'utils/helpers'
import { Downloading } from 'components/Downloading'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { AnalyticsMarketingDatalist } from 'sharedComponents/AnalyticsMarketingDatalist'
import { Box, Container } from 'components/Layout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { SelectInput } from 'components/SelectInput'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import { RESULTS_PER_PAGE } from 'internal-portal/constants/filters'
import { MARKETING_ANALYTICS } from './operations'

type MarketingProps = RouteComponentProps

type FilterState = {
  location?: string
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  memberLike?: string
  user?: string
  resultsPerPage?: string
}

const MarketingAnalytics: React.VFC<MarketingProps> = () => {
  const { hasPermission } = useAuth()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const searchState = useSearchState<FilterState>()
  const { sorter, handleSortChange } = useSortChangeHandler()
  const [downloadableDataUrl, setDownloadableDataUrl] = React.useState<string>()
  const [download, setDownload] = React.useState(false)

  const marketingQuery = useMarketingAnalytics({
    filter: Object.entries({
      memberLike: searchState.data.memberLike,
    }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {}),
    sorter,
    page: Number(searchState.data.page) || 1,
    pageLength: Number(searchState.data.resultsPerPage) || Number(RESULTS_PER_PAGE[0].value),
  })

  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  const [getMarketingCsv, { data: marketingCsv, loading: marketingCsvLoading }] = useLazyQuery<
    MarketingAnalyticsCsv,
    MarketingAnalyticsVariablesCsv
  >(MARKETING_ANALYTICS)

  React.useEffect(() => {
    if (download) {
      const marketing = marketingCsv?.marketingAnalytics.users.map(
        (user: MarketingAnalytics_marketingAnalytics_users) => {
          const { firstName, lastName, birthDate, email, gender, Address, race, ethnicity } = user
          const patient = `${firstName || ''} ${lastName || ''}`
          const patientCity = Address?.city || ''
          const patientState = Address?.state || ''
          const birth = getFormattedDateAndTime(birthDate)
          const age = differenceInCalendarYears(new Date(), new Date(birth))
          const userLine = {
            patient,
            patientGender: gender,
            age,
            email,
            patientCity,
            patientState,
            patientEthnicity: ethnicity,
            patientRace: race,
          }
          return userLine
        }
      )
      if (marketing && !downloadableDataUrl) {
        const csv = papa.unparse(marketing, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `marketing-optIn-${Date()}.csv`)
        document.body.appendChild(link)
        link.click()
        setDownload(false)
      } else if (downloadableDataUrl) {
        window.URL.revokeObjectURL(downloadableDataUrl)
        setDownloadableDataUrl('')
        setDownload(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketingCsv])

  const handleDownloadCsv = () => {
    getMarketingCsv({
      variables: {
        filterData: {
          user: searchState.data.memberLike
            ? { emailOrNameLike: searchState.data.memberLike }
            : null,
        },
        pagination: {
          from: 0,
          length:
            marketingQuery.data?.marketingAnalytics.pagination.total ||
            Number(RESULTS_PER_PAGE[0].value),
          sortBy: 'id',
        },
      },
    })
    setDownload(true)
  }

  return (
    <DashboardPage
      Header={
        hasPermission([UserPermissions.DOWNLOAD_OPTIN_MARKETING_ANALYTICS_CSV]) && (
          <Box display="flex" justifyContent="flex-end" p="1rem">
            <Button appearance="filled" onClick={handleDownloadCsv} disabled={marketingCsvLoading}>
              {marketingCsvLoading ? <Downloading /> : 'Download csv'}
            </Button>
          </Box>
        )
      }
    >
      <Container>
        <AnalyticsMarketingDatalist
          users={marketingQuery.data?.marketingAnalytics.users || []}
          pagination={marketingQuery.data?.marketingAnalytics.pagination}
          isLoading={marketingQuery.loading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <TextInput
                name="memberLike"
                label="Member Name/Email"
                value={filterData.memberLike || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by name/email"
                hasBottomContent={false}
              />
              <SelectInput
                name="resultsPerPage"
                label="Results per page"
                value={filterData.resultsPerPage || ''}
                onChange={handleInputChange}
                options={RESULTS_PER_PAGE}
                hasBottomContent={false}
              />
            </Box>
          }
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={marketingQuery.data?.marketingAnalytics.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default MarketingAnalytics
